import { Avatar, Button, keyframes, Paper, Typography } from '@mui/material';
import { IRecommendations } from 'API/Home/recommendations';
import {
  ArrowBackBlue,
  BlackX,
  Cart,
  equipmentIcon1,
  Expand,
  OrderTreatments,
  PlusInsideSquare,
  SquareArrowCorner,
  UniversalRequestSubject7,
} from 'Images';
import { RecommendationInformation } from './RecoomendationInformation';
import { useState } from 'react';
import { DeleteRecommendation } from './DeleteRecommendation';
import { Buttons, IButtonSubject } from './Buttons';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { RECOMMENDATIONS_TYPES } from 'Enum/recommendation';
import { useNavigate } from 'react-router-dom';

interface IRecommendationCard {
  recommendationItem: IRecommendations;
  removeRecommendation: Function;
  setShowDialog?: () => void;
}

interface ISubjectTypeObject {
  icon: string;
  deleteReqPath: string;
  deleteBodyReq?: any;
  btnLeft: IButtonSubject;
  btnRight?: IButtonSubject;
}

export const RecommendationCard = ({
  recommendationItem,
  removeRecommendation,
  setShowDialog,
}: IRecommendationCard) => {
  const [isFront, setIsFront] = useState<boolean>(true);
  const [animation, setAnimation] = useState<any>(null);

  const showMedicationListButton = recommendationItem.entityJSON?.items?.some(
    (item) => item.medicationName || item.medicationCode
  );

  const navigate = useNavigate();

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Home
  ).userRecommendations;

  const rotateCardStepOne = keyframes`from{ transform: rotateY(0)} to{transform: rotateY(90deg)}`;
  const rotateCardStepTwo = keyframes`from{transform: rotateY(90deg)} to{transform: rotateY(0)}`;

  const animationHandler = (showFront: boolean) => {
    setAnimation(rotateCardStepOne);
    setTimeout(() => {
      setIsFront(showFront);
      setAnimation(rotateCardStepTwo);
    }, 450);
    setTimeout(() => {
      setAnimation(null);
    }, 1000);
  };

  const subjectTypeObjectCreatorHandler = (
    subjectId: number
  ): ISubjectTypeObject | null => {
    if (!recommendationItem || !recommendationItem.entityJSON) {
      return null;
    }

    switch (subjectId) {
      case RECOMMENDATIONS_TYPES.LONG_TERM_EQUIPMENT: {
        return {
          icon: Cart,
          deleteReqPath: 'long-term-equip/close',
          deleteBodyReq: {
            reqNumber:
              recommendationItem.entityJSON.items?.[0]?.reqNumber?.toString() ??
              '',
            nextEntitleDate:
              recommendationItem.entityJSON.items?.[0]?.nextEntitleDate ?? '',
          },
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.claimedLongTermEquipment,
            onClick: () => navigate('/longterm-equipment'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestReferral,
            onClick: () => navigate('universalRequest/1/2'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.MEDICATIONS_EXPIRE_MSG_SINGLE: {
        return {
          icon: UniversalRequestSubject7,
          deleteReqPath: 'expired-meds/close',
          deleteBodyReq: {
            medicationCodes: recommendationItem.entityJSON.items.map(
              (item) => item.medicationCode
            ),
          },
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.uploadNewPrescription,
            onClick: () => navigate('universalRequest/1/6/94'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestPrescription,
            onClick: () => navigate('/universalRequest/1/1/77'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.MEDICATIONS_EXPIRE_MSG_GROUP: {
        return {
          icon: UniversalRequestSubject7,
          deleteReqPath: 'expired-meds/close',
          deleteBodyReq: {
            medicationCodes: recommendationItem.entityJSON.items.map(
              (item) => item.medicationCode
            ),
          },
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.requestPrescription,
            onClick: () => navigate('universalRequest/1/1/77'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.uploadNewPrescription,
            onClick: () => navigate('/universalRequest/1/6/94'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.CONSUMEQUIP_ORDER_REMINDER_SINGLE: {
        return {
          icon: equipmentIcon1,
          deleteReqPath: `close/${recommendationItem.id}`,
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.duplicateOrder,
            onClick: () => navigate('/equipment'),
          },
          btnRight: {
            icon: PlusInsideSquare,
            text: dictionary.newOrder,
            onClick: () => navigate('Equipment/createOrder'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.TREATMENTS_ORDER_REMINDER_SINGLE: {
        return {
          icon: OrderTreatments,
          deleteReqPath: `close/${recommendationItem.id}`,
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.treatmentsInfo,
            onClick: () => navigate('/treatment'),
          },
        };
      }
      default:
        return null;
    }
  };

  const subjectTypeObject: ISubjectTypeObject | null =
    subjectTypeObjectCreatorHandler(recommendationItem.subjectId);

  if (!subjectTypeObject) return null;

  return (
    <Paper
      sx={{
        position: 'relative',
        p: '20px 0 0 0',
        minWidth: isDesktop ? '388px' : '100%',
        minHeight: '215px',
        maxWidth: '388px',
        display: 'flex',
        flexFlow: 'column',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'primary.shade_200',
        bgcolor: isFront ? '#FFF' : '#E7EDFF',
        boxShadow: 'none',
        animation: animation ? `${animation} .5s linear` : undefined,
      }}
    >
      {isFront && (
        <Avatar
          alt={dictionary.deleteBtnAlt}
          src={BlackX}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: '22px',
            height: '22px',
            p: '3px',
            ':hover': {
              cursor: 'pointer',
              outline: '1px solid black',
              borderRadius: '50%',
            },
          }}
          onClick={() => animationHandler(false)}
          tabIndex={0}
        />
      )}
      {isFront ? (
        <>
          <RecommendationInformation
            title={recommendationItem.title}
            description={recommendationItem.description}
            icon={subjectTypeObject.icon}
            createDate={recommendationItem.createDate}
          />

          {showMedicationListButton && (
            <Button
              sx={{
                p: 0,
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '6px',
                mt: '4px',
                position: 'relative',
                bottom: '15px',
                left: '75px',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              onClick={setShowDialog}
            >
              <Typography
                variant='body5'
                sx={{
                  textDecorationLine: isDesktop ? 'underline' : undefined,
                }}
              >
                {dictionary.openList}
              </Typography>
            </Button>
          )}
        </>
      ) : (
        <DeleteRecommendation
          recommendationId={recommendationItem.identifier}
          animationHandler={animationHandler}
          removeRecommendation={removeRecommendation}
          deleteRequest={{
            path: subjectTypeObject.deleteReqPath,
            body: subjectTypeObject.deleteBodyReq,
          }}
        />
      )}
      {isFront && (
        <Buttons
          leftBtn={subjectTypeObject.btnLeft}
          rightBtn={subjectTypeObject.btnRight}
        />
      )}
    </Paper>
  );
};
